import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from '../../global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  addFirmwareCompanyAPI,
  createFirmware as createFirmwareAPI,
  deleteFirmware as deleteFirmwareAPI,
  getFirmware,
  getFirmwares,
  removeFirmwareCompanyAPI,
  updateFirmware as updateFirmwareAPI,
} from './firmwares.api';
import { Firmware } from './firmwares.types';

const name = 'firmwares';

const firmwareEntityAdapter = createEntityAdapter<AsyncEntity<Firmware>>({
  selectId: firmware => firmware.id,
});

export const firmwareSelectors = firmwareEntityAdapter.getSelectors();

export const fetchFirmwares = createAsyncThunkFromFetch(`${name}/fetchFirmwares`, getFirmwares);
export const deleteFirmware = createAsyncThunkFromFetch(`${name}/deleteFirmware`, deleteFirmwareAPI);
export const createFirmware = createAsyncThunkFromFetch(`${name}/createFirmware`, createFirmwareAPI);
export const fetchFirmware = createAsyncThunkFromFetch(`${name}/fetchFirmware`, getFirmware);
export const updateFirmware = createAsyncThunkFromFetch(`${name}/updateFirmware`, updateFirmwareAPI);
export const addFirmwareCompany = createAsyncThunkFromFetch(`${name}/addFirmwareCompany`, addFirmwareCompanyAPI);
export const removeFirmwareCompany = createAsyncThunkFromFetch(
  `${name}/removeFirmwareCompany`,
  removeFirmwareCompanyAPI
);

const firmwaresSlice = createSlice({
  name,
  initialState: firmwareEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchFirmwares.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchFirmwares.fulfilled, (state, { payload }) => {
      firmwareEntityAdapter.upsertMany(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchFirmwares.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteFirmware.pending, (state, { meta }) => {
      firmwareEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteFirmware.fulfilled, (state, { meta }) => {
      firmwareEntityAdapter.removeOne(state, meta.arg.id);
    });
    builder.addCase(deleteFirmware.rejected, (state, { meta }) => {
      firmwareEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(fetchFirmware.fulfilled, (state, { payload }) => {
      firmwareEntityAdapter.upsertOne(state, {
        ...payload,
        isPending: false,
        hasError: false,
      });
    });
    builder.addCase(updateFirmware.fulfilled, (state, { payload, meta }) => {
      const { id } = meta.arg;
      firmwareEntityAdapter.updateOne(state, {
        id,
        changes: payload,
      });
    });
    builder.addCase(addFirmwareCompany.fulfilled, (state, { payload, meta }) => {
      const { id } = meta.arg;
      const item = firmwareSelectors.selectById(state, id) as Firmware;
      firmwareEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: {
          companies: [...item.companies, payload],
        },
      });
    });
    builder.addCase(removeFirmwareCompany.fulfilled, (state, { meta }) => {
      const { id } = meta.arg;
      const item = firmwareSelectors.selectById(state, id) as Firmware;
      firmwareEntityAdapter.updateOne(state, {
        id: meta.arg.id,
        changes: {
          companies: item.companies.filter(c => c.id !== meta.arg.companyId),
        },
      });
    });
  },
});

const { reducer } = firmwaresSlice;

export default reducer;
